<template>
    <div class="ppv2-flex lg:ppv2-flex-row ppv2-flex-col ppv2-w-full ppv2-step-wrapper ticket-options-wrapper">
        <div class="ticket-form-section ppv2-flex ppv2-flex-col">
            <div class="ppv2-flex ppv2-flex-col lg:ppv2-flex-row ppv2-flex-1 ppv2-overflow-hidden">
                <!-- SIDEBAR: List of variants -->
                <div
                    class="ticket-variants-sidebar variant-sidebar ppv2-flex ppv2-flex-col ppv2-shrink-0"
                    :style="{ padding: '70px ' + normalizeSize(20, true) + ' ' + normalizeSize(20, true) }"
                >
                    <p class="variant-sidebar-heading">
                        {{ $t('experience.stepTicketOptions.variantsTitle') }}
                    </p>

                    <div class="variant-panel bg-purple ppv2-bg-opacity-[7%] ppv2-space-y-3">
                        <div class="variant-list">
                            <div
                                v-for="(variant, vIndex) in variants"
                                :key="vIndex"
                                class="variant-row"
                                :class="{
                                    'variant-row--active ppv2-bg-white dark:ppv2-bg-dark': selectedIndex === vIndex,
                                    'drag-over': dragOverIndex === vIndex,
                                }"
                                draggable="true"
                                @click="selectVariant(vIndex)"
                                @dragstart="onVariantDragStart($event, vIndex)"
                                @dragover="onVariantDragOver($event)"
                                @drop="onVariantDrop($event, vIndex)"
                                @dragenter="onVariantDragEnter($event, vIndex)"
                                @dragleave="onVariantDragLeave($event)"
                                @dragend="onVariantDragEnd($event)"
                            >
                                <div class="variant-row-inner">
                                    <div class="variant-row-body">
                                        <div class="variant-row-name ppv2-text-black">
                                            {{ variant.name || $t('experience.stepTicketOptions.sansNom') }}
                                            <span
                                                v-if="hasVariantErrors(vIndex)"
                                                class="ppv2-text-red-500 poppinsBold ppv2-text-base ppv2-leading-none"
                                                >•</span
                                            >
                                        </div>
                                        <div class="variant-row-meta">
                                            <span class="variant-row-price">{{ getVariantPriceDisplay(variant) }}</span>
                                            <span
                                                v-if="getVariantRulesCount(variant) >= 1"
                                                class="variant-row-rules text-purple ppv2-text-opacity-90"
                                                >·
                                                {{
                                                    $tc(
                                                        'experience.stepTicketOptions.rulesCount',
                                                        getVariantRulesCount(variant),
                                                        { count: getVariantRulesCount(variant) }
                                                    )
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        class="variant-kebab text-purple ppv2-text-opacity-80 hover:ppv2-text-opacity-100 hover:ppv2-bg-opacity-[5%]"
                                        :aria-label="$t('common.options')"
                                        @click.stop="toggleVariantMenu(vIndex)"
                                    >
                                        <MoreVertical :size="14" />
                                    </button>
                                </div>

                                <!-- Dropdown menu -->
                                <div
                                    v-if="activeVariantMenu === vIndex"
                                    class="menuDropdown ppv2-absolute ppv2-right-2 ppv2-top-8 !ppv2-w-[max-content]"
                                >
                                    <button
                                        class="ppv2-w-full ppv2-text-sm poppinsSemiBold ppv2-text-left ppv2-px-4 ppv2-py-1"
                                        @click.stop="duplicateVariant(vIndex)"
                                    >
                                        <Copy :size="14" />
                                        <span>{{ $t('experience.stepTicketOptions.copy') }}</span>
                                    </button>
                                    <button
                                        class="ppv2-w-full ppv2-text-sm poppinsSemiBold ppv2-text-left ppv2-px-4 ppv2-py-1"
                                        @click.stop="removeVariant(vIndex)"
                                        v-if="variants.length > 1"
                                    >
                                        <Trash2 :size="14" />
                                        <span>{{ $t('experience.stepTicketOptions.delete') }}</span>
                                    </button>
                                </div>
                            </div>
                            <button
                                class="add-variant-sidebar-btn text-purple hover:ppv2-bg-opacity-[7%]"
                                :style="{ fontSize: normalizeFontSize(12) }"
                                @click="addVariant"
                            >
                                <Plus :size="13" style="min-width: 13px" />
                                <span>{{ $t('experience.stepTicketOptions.addVariant') }}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- DETAIL PANEL: Configuration of selected variant -->
                <div class="variant-detail-panel ppv2-flex-1 ppv2-overflow-y-auto ppv2-px-4 lg:ppv2-pt-[70px] ppv2-pt-[35px]">
                    <HeaderStep :step="step" :sizes="sizes" />

                    <!-- SECTION 1: Variant Information -->
                    <div
                        class="ppv2-bg-black ppv2-bg-opacity-[2%] ppv2-border ppv2-border-[#9C9AF833] ppv2-rounded-[16px] ppv2-px-6 ppv2-py-5 ppv2-mb-4"
                    >
                        <div class="poppinsSemiBold ppv2-text-black ppv2-mb-4" :style="{ fontSize: normalizeFontSize(15.5) }">
                            {{ $t('experience.stepTicketOptions.variantInfo') }}
                        </div>
                        <div class="ppv2-grid xl:ppv2-grid-cols-4 ppv2-grid-cols-1 md:ppv2-gap-6 ppv2-gap-2">
                            <div class="ppv2-col-span-2">
                                <CustomInput
                                    v-model="selectedVariant.name"
                                    :label="`${$t('experience.stepTicketOptions.variantName')}`"
                                    :placeholder="
                                        $t('experience.stepTicketOptions.variantNamePlaceholder') + ' ' + (selectedIndex + 1)
                                    "
                                    :state="getVariantState(selectedIndex, 'name')"
                                    :errorText="getVariantError(selectedIndex, 'name')"
                                    @input="clearVariantError(selectedIndex, 'name')"
                                    :floatingLabel="true"
                                    :required="true"
                                    :customSizeLabel="12"
                                />
                            </div>
                            <CustomInput
                                v-model="selectedVariant.price"
                                :label="`${$t('experience.stepTicketOptions.price')}`"
                                :rightIcon="$symbolCurrency"
                                type="number"
                                :min="0"
                                :state="getVariantState(selectedIndex, 'price')"
                                :errorText="getVariantError(selectedIndex, 'price')"
                                @input="clearVariantError(selectedIndex, 'price')"
                                :floatingLabel="true"
                                :required="true"
                                :customSizeLabel="12"
                            />
                            <BaseSelect
                                v-model="selectedVariant.tva"
                                :label="`${$t('experience.stepTicketOptions.tva')}`"
                                :required="true"
                                :options="option_tva"
                                :searchable="false"
                                option-label="label"
                                option-value="value"
                                :error="getVariantError(selectedIndex, 'tva')"
                                @input="clearVariantError(selectedIndex, 'tva')"
                                :customSizeLabel="12"
                                :floatingLabel="true"
                                :floatingLabelBg="theme.mode === 'dark' ? '#151933' : '#fff'"
                            />
                        </div>
                    </div>

                    <!-- SECTION 2: Tarifs Dégressifs -->
                    <SectionCard
                        v-model="selectedVariant.hasDegressif"
                        title=""
                        class="ppv2-mb-4 ticket-section-card"
                        customBg="ppv2-bg-black ppv2-bg-opacity-[2%]"
                        :showSwitchLabel="false"
                    >
                        <template #header-left>
                            <div class="ppv2-flex ppv2-items-center ppv2-gap-4">
                                <div
                                    :class="{
                                        'bg-purple': selectedVariant.hasDegressif,
                                        'ppv2-bg-black ppv2-bg-opacity-[10%]': !selectedVariant.hasDegressif,
                                    }"
                                    class="ppv2-p-2 ppv2-rounded-[10px] ppv2-flex ppv2-items-center ppv2-justify-center"
                                >
                                    <ArrowUpDown
                                        :size="18"
                                        :class="{
                                            '!ppv2-text-white': selectedVariant.hasDegressif,
                                            'ppv2-text-black': !selectVariant.hasDegressif,
                                        }"
                                    />
                                </div>
                                <div class="ppv2-flex ppv2-flex-col">
                                    <span class="poppinsSemiBold ppv2-text-base ppv2-text-black">{{
                                        $t('experience.stepTicketOptions.degressifTitle')
                                    }}</span>
                                    <span class="ppv2-text-[11px] ppv2-text-gray-500 poppinsRegular">
                                        {{ $t('experience.stepTicketOptions.degressifSubtitle') }}
                                    </span>
                                </div>
                            </div>
                        </template>

                        <div class="degressif-content">
                            <div class="ppv2-flex ppv2-flex-col ppv2-gap-4">
                                <div
                                    v-for="(palier, pIndex) in selectedVariant.paliers"
                                    :key="pIndex"
                                    class="ppv2-border ppv2-border-black ppv2-border-opacity-[6%] ppv2-rounded-[12px] md:ppv2-p-3 ppv2-p-1 ppv2-flex ppv2-flex-col xl:ppv2-flex-row ppv2-items-center ppv2-w-full"
                                    :style="{
                                        gap: normalizeSize(24, true),
                                    }"
                                >
                                    <!-- Palier Index -->
                                    <div
                                        class="palier-index poppinsBold ppv2-text-sm ppv2-text-white bg-purple ppv2-rounded-[8px] ppv2-w-[30px] ppv2-h-[30px] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-shrink-0"
                                    >
                                        {{ pIndex + 1 }}
                                    </div>

                                    <!-- Inputs Group -->
                                    <div
                                        class="ppv2-flex-1 ppv2-flex ppv2-flex-col xl:ppv2-flex-row ppv2-items-center"
                                        :style="{
                                            gap: normalizeSize(24, true),
                                        }"
                                    >
                                        <!-- NB Billets -->
                                        <div class="ppv2-flex-1">
                                            <CustomInput
                                                v-model="palier.nb_billets"
                                                :label="$t('experience.stepTicketOptions.startingFrom')"
                                                :rightIcon="$t('experience.stepTicketOptions.tickets')"
                                                type="number"
                                                :min="1"
                                                @input="clearPalierError(pIndex, 'nb_billets')"
                                                :state="getPalierState(pIndex, 'nb_billets')"
                                                :errorText="getPalierError(pIndex, 'nb_billets')"
                                                :floatingLabel="true"
                                                :floatingLabelBg="theme.mode === 'dark' ? '#151933' : '#fff'"
                                                classContainerAdd="!ppv2-mb-0"
                                                :customSizeLabel="12"
                                            />
                                        </div>

                                        <!-- Price -->
                                        <div class="ppv2-flex-1">
                                            <CustomInput
                                                v-model="palier.price"
                                                :label="$t('experience.stepTicketOptions.unitPrice')"
                                                :rightIcon="$symbolCurrency"
                                                type="number"
                                                :min="0"
                                                @input="clearPalierError(pIndex, 'price')"
                                                :state="getPalierState(pIndex, 'price')"
                                                :errorText="getPalierError(pIndex, 'price')"
                                                :floatingLabel="true"
                                                :floatingLabelBg="theme.mode === 'dark' ? '#151933' : '#fff'"
                                                classContainerAdd="!ppv2-mb-0"
                                                :customSizeLabel="12"
                                            />
                                        </div>
                                    </div>

                                    <!-- Right side: Discount + Actions -->
                                    <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-4">
                                        <!-- Discount badge -->
                                        <div
                                            v-if="getPalierDiscount(pIndex) > 0"
                                            class="ppv2-bg-[#10B981]/12 ppv2-border ppv2-border-[#10B981]/30 ppv2-text-[#34D399] ppv2-px-3 ppv2-py-1 ppv2-rounded-full ppv2-text-xs poppinsSemiBold"
                                        >
                                            -{{ getPalierDiscount(pIndex) }}%
                                        </div>
                                        <div v-else class="xl:ppv2-w-[60px]"></div>
                                        <!-- Spacer to keep alignment if no discount -->

                                        <!-- Actions -->
                                        <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                                            <button
                                                @click="duplicatePalier(pIndex)"
                                                class="ppv2-text-gray-400 hover:ppv2-text-primary ppv2-transition-colors"
                                            >
                                                <Copy :size="18" />
                                            </button>
                                            <button
                                                @click="removePalier(pIndex)"
                                                class="ppv2-text-gray-400 hover:ppv2-text-red-500 ppv2-transition-colors"
                                            >
                                                <Trash2 :size="18" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    class="add-palier-dash-btn ppv2-text-sm poppinsRegular ppv2-mt-2 ppv2-w-full"
                                    @click="addPalier"
                                >
                                    <Plus :size="16" />
                                    <span>{{ $t('experience.stepTicketOptions.addPalier') }}</span>
                                </button>
                            </div>
                        </div>
                    </SectionCard>

                    <!-- SECTION 3: Prix Spécifiques -->
                    <SectionCard
                        v-model="selectedVariant.hasSpecificPricing"
                        title=""
                        customBg="ppv2-bg-black ppv2-bg-opacity-[2%]"
                        class="ppv2-mb-4 ticket-section-card"
                        :showSwitchLabel="false"
                    >
                        <template #header-left>
                            <div class="ppv2-flex ppv2-items-center ppv2-gap-4">
                                <div
                                    :class="{
                                        'bg-purple': selectedVariant.hasSpecificPricing,
                                        'ppv2-bg-black ppv2-bg-opacity-[10%]': !selectedVariant.hasSpecificPricing,
                                    }"
                                    class="ppv2-p-2 ppv2-rounded-[10px] ppv2-flex ppv2-items-center ppv2-justify-center"
                                >
                                    <Calendar
                                        :size="18"
                                        :class="{
                                            '!ppv2-text-white': selectedVariant.hasSpecificPricing,
                                            'ppv2-text-black': !selectedVariant.hasSpecificPricing,
                                        }"
                                    />
                                </div>
                                <div class="ppv2-flex ppv2-flex-col">
                                    <span class="poppinsSemiBold ppv2-text-base ppv2-text-black">{{
                                        $t(
                                            'experience.stepTicketOptions.specificPricingTitle',
                                            'Prix spécifiques par jour / heure'
                                        )
                                    }}</span>
                                    <span class="ppv2-text-[11px] ppv2-text-gray-500 poppinsRegular">
                                        {{
                                            $t(
                                                'experience.stepTicketOptions.specificPricingSubtitle',
                                                'Faites varier vos tarifs pour des heures/jours spécifiques.'
                                            )
                                        }}
                                    </span>
                                </div>
                            </div>
                        </template>

                        <div class="specific-pricing-content">
                            <!-- Le mode de l'étape « Disponibilités » pilote la forme des tarifs (serveur : Step5Request) -->
                            <p
                                v-if="selectedVariant.hasSpecificPricing"
                                class="ppv2-text-[11px] ppv2-text-gray-500 poppinsRegular ppv2-mb-2"
                            >
                                {{ pricingModeLabel }}
                            </p>
                            <div v-if="selectedVariantTarifErrors.length" class="ppv2-text-red-500 ppv2-text-sm ppv2-mb-2">
                                <p v-for="(message, i) in selectedVariantTarifErrors" :key="i">{{ message }}</p>
                            </div>
                            <CalendarFull
                                v-if="selectedVariant.hasSpecificPricing"
                                ref="planningCalendar"
                                :establishmentId="establishmentId"
                                :prestation="selectedVariant"
                                :externalEvents="planningEvents"
                                :disableInternalDialog="true"
                                @date-click="onPlanningDateClick"
                                @event-click="onPlanningEventClick"
                                @checked-dates-change="planningCheckedDates = $event"
                            />

                            <div v-if="planningCheckedDates.length > 0" class="ppv2-flex ppv2-justify-center ppv2-mt-4">
                                <BaseButton
                                    type="button"
                                    variant="secondary"
                                    class="!ppv2-rounded-full !ppv2-px-8"
                                    @click="openPlanningManual"
                                    :customFontSize="12"
                                >
                                    {{ $t('experience.stepTicketOptions.configureSelectedDates') }}
                                </BaseButton>
                            </div>

                            <PlanningModal
                                v-model="showPlanningModal"
                                apiMode="emit"
                                :wholeDay="planningWholeDay"
                                :presetSlots="presetSlots"
                                :hint="planningHint"
                                :resetBlocked="planningResetBlocked"
                                :prestationId="selectedVariant.id"
                                :establishmentId="establishmentId"
                                :selectedSlot="planningSelectedSlot"
                                :mode="planningMode"
                                :prestation="selectedVariant"
                                @submit-rule="onPlanningSubmitRule"
                                @cancel="showPlanningModal = false"
                            />
                        </div>
                    </SectionCard>

                    <!-- SECTION 4: Advanced Options -->
                    <DashedCard
                        :marginTop="0"
                        :marginBottom="32"
                        :custom-style="{ padding: normalizeSize(24, true) }"
                        customClass="!ppv2-border-[#545595]"
                    >
                        <template #title>
                            <span class="poppinsMedium ppv2-text-black ppv2-px-1">{{
                                $t('experience.stepTicketOptions.advancedOptions')
                            }}</span>
                        </template>
                        <template #content>
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-mt-2">
                                <BaseSwitch
                                    v-model="selectedVariant.isUndated"
                                    :label="$t('experience.stepTicketOptions.undatedLabel')"
                                />
                            </div>
                        </template>
                    </DashedCard>
                    <!-- FOOTER ACTIONS -->
                    <div
                        class="form-actions !ppv2mb-0 !ppv2-static !ppv2-mt-auto ppv2-pt-4 ppv2-bg-white dark:ppv2-bg-dark ppv2-z-10"
                        :class="{ '!ppv2-justify-end': complited }"
                    >
                        <BackButton v-show="!complited" type="button" @click="$emit('previous-step')" />
                        <BaseButton id="submit" type="submit" variant="primary" @click="submitForm" :loading="loading">
                            {{ !complited ? $t('experience.next') : $t('common.save') }}
                        </BaseButton>
                    </div>
                </div>
            </div>
        </div>

        <div class="lg:ppv2-pt-[70px] ppv2-pt-[35px]">
            <PreviewExperience v-if="!hidePreview" :experience="prestationForPreview" :activeField="activeField" :step="step" />
        </div>
    </div>
</template>

<script>
import HeaderStep from './HeaderStep.vue'
import SectionCard from '../../Website/Settings/components/SectionCard.vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import BackButton from '../../../Shared/BackButton.vue'
import BaseButton from '../../../Shared/BaseButton.vue'

import BaseSelect from '../../../Shared/BaseSelect.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseBooleanToggle from '../../../Shared/BaseBooleanToggle.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import PreviewExperience from './PreviewExperience.vue'
import CalendarFull from '../../../Shared/CalendarFull.vue'
import PlanningModal from './PlanningModal.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import ExperienceMixin from '../mixins/ExperienceMixin'
import { SENTINEL_END_DATE } from '../../../../constants/planning'
import { Plus, Copy, Trash2, Settings, GripVertical, ChevronRight, MoreVertical, Calendar, ArrowUpDown } from 'lucide-vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize.js'

export default {
    name: 'StepTicketOptions',
    components: {
        HeaderStep,
        SectionCard,
        DashedCard,
        BackButton,
        BaseButton,
        BaseSelect,
        CustomInput,
        BaseBooleanToggle,
        BaseSwitch,
        PreviewExperience,
        CalendarFull,
        PlanningModal,
        Plus,
        Copy,
        Trash2,
        Settings,
        GripVertical,
        ChevronRight,
        MoreVertical,
        Calendar,
        ArrowUpDown,
    },
    mixins: [ExperienceMixin],
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        prestation: { type: Object, required: false },
        selected_experience: { type: Object, required: false },
        id: { type: [Number, String], default: null },
        country_vat_rates: { type: Array, required: false },
        complited: { type: Boolean, required: false },
        hidePreview: { type: Boolean, default: false },
    },
    data() {
        return {
            activeField: null,
            loading: false,
            option_tva: [],
            variants: [this.createDefaultVariant(this.$t('experience.stepTicketOptions.variantNamePlaceholder') + ' 1')],
            selectedIndex: 0,
            errors: {},

            // Planning / Specific Pricing
            showPlanningModal: false,
            planningSelectedSlot: null,
            planningMode: 'create',
            planningCheckedDates: [],
            // Vrai quand une règle d'une AUTRE plage couvre les dates ouvertes dans le modal : vider
            // tous les prix ne ramènerait pas ces dates au prix de base (cf. PlanningModal.resetBlocked).
            planningResetBlocked: false,

            // Sidebar UI
            activeVariantMenu: null,

            // Drag & Drop
            draggedVariantIndex: null,
            dragOverIndex: null,
            initialDataString: null,
        }
    },
    computed: {
        selectedVariant() {
            return this.variants[this.selectedIndex] || this.variants[0]
        },
        establishmentId() {
            return this.prestation?.establishment_id ?? 1
        },
        // ── Cohérence avec l'étape « Disponibilités » (availability_mode du billet parent) ──────
        // Le serveur (Step5Request / TicketTarifCoherence) reste seul juge ; ici on n'adapte que la
        // saisie pour qu'un gestionnaire ne puisse pas produire un tarif qui serait refusé.
        ticketPricingMode() {
            const mode = this.prestation?.availability_mode
            return ['all_day', 'fixed', 'custom'].includes(mode) ? mode : null
        },
        // all_day : un prix par jour, sans heures. Un billet historique (sans mode) saisit des plages
        // horaires libres, comme `fixed` et comme le serveur l'accepte (TicketTarifCoherence::conforms) :
        // forcé en journée entière, il ne pouvait pas porter plusieurs tarifs le même jour et ses
        // tarifs horaires déjà en base étaient repliés sur une seule ligne à la réouverture d'un jour.
        planningWholeDay() {
            return this.ticketPricingMode === 'all_day'
        },
        // custom : un prix par créneau de l'étape Disponibilités, heures verrouillées. Une fin au
        // lendemain est plafonnée à 23:59 (colonnes TIME côté serveur, cf. TicketTarifCoherence).
        presetSlots() {
            if (this.ticketPricingMode !== 'custom') return []
            const slots = this.prestation?.availability_slots || this.prestation?.availabilitySlots || []
            return slots.map((s) => {
                const nextDay = Number(s.end_minutes) >= 1440
                return {
                    label: s.name || '',
                    startTime: (s.start || '').substring(0, 5),
                    endTime: nextDay ? '23:59' : (s.end || '').substring(0, 5),
                    nextDay,
                }
            })
        },
        planningHint() {
            // Toute la journée : un seul créneau vendu par jour, tarifé à l'ouverture — un second tarif
            // horaire ne serait jamais facturé. On le dit, et on renvoie vers les créneaux personnalisés.
            if (this.ticketPricingMode === 'all_day') {
                return this.$t('experience.planningModal.allDayHint')
            }
            if (this.ticketPricingMode === 'fixed') {
                return this.$t('experience.planningModal.fixedHint', {
                    duration: this.prestation?.availability_duration || 60,
                })
            }
            if (this.ticketPricingMode === 'custom' && this.presetSlots.some((s) => s.nextDay)) {
                return this.$t('experience.planningModal.nextDayHint')
            }
            if (this.ticketPricingMode === null) {
                return this.$t('experience.planningModal.legacyHint')
            }
            return ''
        },
        pricingModeLabel() {
            const keys = { all_day: 'allDay', fixed: 'fixed', custom: 'custom' }
            const key = keys[this.ticketPricingMode] || 'legacy'
            return this.$t(`experience.stepTicketOptions.pricingMode.${key}`, {
                duration: this.prestation?.availability_duration || 60,
            })
        },
        // Erreurs serveur (422) portant sur les tarifs horaires de la variante affichée.
        selectedVariantTarifErrors() {
            const prefix = `ticket_variants.${this.selectedIndex}.configuredTarifs`
            return Object.keys(this.errors || {})
                .filter((key) => key.startsWith(prefix))
                .map((key) => (Array.isArray(this.errors[key]) ? this.errors[key][0] : this.errors[key]))
        },
        // Règles EFFECTIVES par date. Les tarifs sont regroupés par soumission du modal (même plage,
        // mêmes jours — tarifGroupKey) et, sur une date, seul le DERNIER groupe qui la couvre
        // s'applique : miroir d'affichage de ce que syncConfiguredTarifs écrit
        // (purgeAndSplitIntersecting). Le calendrier et le pré-remplissage du modal lisent cette
        // vue ; configuredTarifs reste la charge utile envoyée au serveur.
        effectivePlanning() {
            const tarifs = (this.selectedVariant && this.selectedVariant.configuredTarifs) || []
            const dayMap = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 }
            const fmt = (d) => {
                const y = d.getFullYear()
                const m = String(d.getMonth() + 1).padStart(2, '0')
                const dd = String(d.getDate()).padStart(2, '0')
                return `${y}-${m}-${dd}`
            }

            // Groupes dans l'ordre de première apparition (même règle que le serveur).
            const groups = new Map()
            tarifs.forEach((t, ti) => {
                const key = this.tarifGroupKey(t)
                if (!groups.has(key)) groups.set(key, [])
                groups.get(key).push({ t, ti })
            })

            const addDays = (d, n) => {
                const out = new Date(d)
                out.setDate(out.getDate() + n)
                return out
            }
            const today = new Date()
            today.setHours(0, 0, 0, 0)

            const byDate = new Map()
            groups.forEach((items) => {
                const dates = new Set()
                items.forEach(({ t }) => {
                    const start = t.dateStart ? String(t.dateStart).slice(0, 10) : null
                    if (!start) return
                    // Sans date de fin (ligne sentinelle) : la règle court indéfiniment ; on ne
                    // matérialise que la fenêtre utile au calendrier (un mois en arrière, un an devant).
                    const openEnded = !t.dateEnd
                    const end = openEnded ? fmt(addDays(today, 366)) : String(t.dateEnd).slice(0, 10)
                    if (!openEnded && start === end) {
                        dates.add(start)
                        return
                    }
                    const from = openEnded && start < fmt(addDays(today, -31)) ? fmt(addDays(today, -31)) : start
                    const wanted =
                        Array.isArray(t.selectedDays) && t.selectedDays.length
                            ? new Set(
                                  t.selectedDays
                                      .flatMap((d) => (typeof d === 'string' ? d.split(',').map((s) => s.trim()) : [d]))
                                      .map((d) => dayMap[d])
                                      .filter((v) => v !== undefined)
                              )
                            : null
                    const cursor = new Date(`${from}T00:00:00`)
                    const e = new Date(`${end}T00:00:00`)
                    let g = 0
                    while (cursor <= e && g < 400) {
                        if (!wanted || wanted.has(cursor.getDay())) dates.add(fmt(cursor))
                        cursor.setDate(cursor.getDate() + 1)
                        g++
                    }
                })
                // Le dernier groupe couvrant la date remplace les précédents.
                dates.forEach((ds) => byDate.set(ds, items))
            })
            return byDate
        },
        // Un événement de calendrier par (date, créneau effectif), libellé par son prix — la forme
        // que CalendarFull rend de façon fiable (allDay, un jour). L'index d'origine reste dans l'id
        // pour que deux prix d'un même jour ne se dédoublonnent pas.
        planningEvents() {
            const out = []
            this.effectivePlanning.forEach((items, ds) => {
                items.forEach(({ t, ti }) => {
                    const baseId = `${t.id || t.planningId || 'loc'}_${ti}`
                    out.push({
                        ...t,
                        id: `${baseId}_${ds}`,
                        dateStart: ds,
                        dateEnd: ds,
                        eventName: this.formatTarifPrice(t.price),
                        timeRange: this.isWholeDayTarif(t) ? '' : `${t.timeStart} – ${t.timeEnd}`,
                        title: this.tarifEventTitle(t),
                    })
                })
            })
            return out
        },
        prestationForPreview() {
            return {
                ...(this.prestation || {}),
                ticket_variants: this.variants,
            }
        },
    },
    methods: {
        normalizeSize,
        normalizeFontSize,
        normalizeSizeVertical,
        createDefaultVariant(name = '') {
            return {
                id: null,
                name: name,
                price: '',
                tva: '',
                hasDegressif: false,
                paliers: [{ nb_billets: 1, price: '' }],
                hasSpecificPricing: false,
                configuredTarifs: [],
                isUndated: false,
            }
        },
        selectVariant(index) {
            this.selectedIndex = index
        },
        toggleVariantMenu(index) {
            this.activeVariantMenu = this.activeVariantMenu === index ? null : index
        },
        addVariant() {
            const newName = `${this.$t('experience.stepTicketOptions.variantNamePlaceholder')} ${this.variants.length + 1}`
            const variant = this.createDefaultVariant(newName)
            this.variants.push(variant)
            this.selectedIndex = this.variants.length - 1
        },
        async duplicateVariant(index) {
            const source = this.variants[index]
            // We need to save the variant first to get an ID if we want to copy specific prices?
            // Actually, for now, let's just copy the UI state.
            const copy = JSON.parse(JSON.stringify(source))
            copy.id = null
            copy.name = source.name ? `${source.name} (${this.$t('experience.stepTicketOptions.copy').toLowerCase()})` : ''
            this.variants.splice(index + 1, 0, copy)
            this.selectedIndex = index + 1
            this.activeVariantMenu = null
        },
        removeVariant(index) {
            if (this.variants.length <= 1) return
            this.variants.splice(index, 1)
            if (index === this.selectedIndex) {
                this.selectedIndex = Math.min(this.selectedIndex, this.variants.length - 1)
            } else if (index < this.selectedIndex) {
                this.selectedIndex--
            }
            this.activeVariantMenu = null
        },
        onVariantDragStart(evt, index) {
            evt.dataTransfer.setData('text/plain', index)
            evt.dataTransfer.effectAllowed = 'move'
            this.draggedVariantIndex = index
        },
        onVariantDragOver(evt) {
            evt.preventDefault()
            evt.dataTransfer.dropEffect = 'move'
        },
        onVariantDrop(evt, targetIndex) {
            evt.preventDefault()
            const fromIndex = this.draggedVariantIndex
            if (fromIndex !== null && fromIndex !== targetIndex) {
                const item = this.variants.splice(fromIndex, 1)[0]
                this.variants.splice(targetIndex, 0, item)
                if (this.selectedIndex === fromIndex) {
                    this.selectedIndex = targetIndex
                } else if (fromIndex < this.selectedIndex && targetIndex >= this.selectedIndex) {
                    this.selectedIndex--
                } else if (fromIndex > this.selectedIndex && targetIndex <= this.selectedIndex) {
                    this.selectedIndex++
                }
            }
            this.dragOverIndex = null
        },
        onVariantDragEnter(evt, index) {
            this.dragOverIndex = index
        },
        onVariantDragLeave(evt) {
            if (!evt.currentTarget.contains(evt.relatedTarget)) {
                this.dragOverIndex = null
            }
        },
        onVariantDragEnd(evt) {
            this.draggedVariantIndex = null
            this.dragOverIndex = null
        },
        addPalier() {
            const paliers = this.selectedVariant.paliers
            const lastNb = paliers.length ? Number(paliers[paliers.length - 1].nb_billets) || 0 : 0
            paliers.push({ nb_billets: lastNb + 1, price: '' })
        },
        removePalier(pIndex) {
            this.selectedVariant.paliers.splice(pIndex, 1)
        },
        duplicatePalier(pIndex) {
            const source = this.selectedVariant.paliers[pIndex]
            this.selectedVariant.paliers.splice(pIndex + 1, 0, { ...source })
        },
        setOptionTva() {
            if (Array.isArray(this.country_vat_rates)) {
                this.option_tva = [
                    { label: this.$t('common.choose'), value: '' },
                    ...this.country_vat_rates.map((tva) => ({
                        label: `${tva.pourcentage}%`,
                        value: tva.pourcentage,
                    })),
                ]
            }
        },

        // Planning methods (Reused from Step5.vue)
        buildSlotFromCheckedDates(fallbackDate) {
            let sorted = []
            let slot = null

            if (this.planningCheckedDates.length > 0) {
                sorted = [...this.planningCheckedDates].sort()
                slot = {
                    dateStr: sorted[0],
                    start: sorted[0],
                    startStr: sorted[0],
                    end: sorted[sorted.length - 1],
                    endStr: sorted[sorted.length - 1],
                }
            } else if (fallbackDate) {
                sorted = [fallbackDate]
                slot = {
                    dateStr: fallbackDate,
                    start: fallbackDate,
                    startStr: fallbackDate,
                    end: fallbackDate,
                    endStr: fallbackDate,
                }
            }

            if (slot) {
                // Tarifs EFFECTIFS des dates cochées (cf. effectivePlanning), puis camelCase →
                // snake_case pour PlanningModal.pushItems / seedFromPresets.
                const matches = this.matchedTarifsForDates(sorted)
                if (matches.length > 0) {
                    slot.planningPrices = matches.map((t) => this.tarifToPlanningPrice(t))
                }
            }
            return slot
        },
        // Tarifs EFFECTIFS couvrant l'une des dates données (cf. effectivePlanning), sans doublon :
        // sur une date, seuls les créneaux de la dernière soumission qui la couvre s'appliquent —
        // exactement ce que le serveur enregistrera et facturera.
        matchedTarifsForDates(dates) {
            const seen = new Set()
            const out = []
            for (const d of dates || []) {
                for (const { t, ti } of this.effectivePlanning.get(d) || []) {
                    if (seen.has(ti)) continue
                    seen.add(ti)
                    out.push(t)
                }
            }
            return out
        },
        // Clé de groupe = une soumission du modal : plage + jours (mêmes règles que le serveur,
        // TicketTarifCoherence::groupKey). Sans date de fin ⇒ plage ouverte, tous les jours.
        tarifGroupKey(t) {
            const ALL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
            const start = t.dateStart ? String(t.dateStart).slice(0, 10) : ''
            const end = t.dateEnd ? String(t.dateEnd).slice(0, 10) : SENTINEL_END_DATE
            const raw = t.dateEnd && Array.isArray(t.selectedDays) ? t.selectedDays : []
            const wanted = new Set(
                raw
                    .flatMap((d) => (typeof d === 'string' ? d.split(',').map((s) => s.trim()) : [d]))
                    .filter((d) => ALL.includes(d))
            )
            const days = wanted.size === 0 || wanted.size === 7 ? ALL : ALL.filter((d) => wanted.has(d))
            return `${start}|${end}|${days.join(',')}`
        },
        // Tarif « journée entière » : le mode l'impose (all_day) ou ses bornes le disent (00:00–23:59,
        // cas d'un billet sans mode dont les prix ont été saisis à la journée). Affiché sans plage.
        isWholeDayTarif(t) {
            if (this.planningWholeDay) return true
            return (t.timeStart || '').substring(0, 5) === '00:00' && (t.timeEnd || '').substring(0, 5) === '23:59'
        },
        formatTarifPrice(price) {
            return `${Number(price || 0).toFixed(2)} ${this.$symbolCurrency || '€'}`
        },
        // Libellé complet (info-bulle) : « Matin • 48.00 € » en créneaux perso, sinon prix + plage.
        tarifEventTitle(t) {
            const price = this.formatTarifPrice(t.price)
            if (this.isWholeDayTarif(t)) return price
            const start = (t.timeStart || '').substring(0, 5)
            const end = (t.timeEnd || '').substring(0, 5)
            const preset = this.presetSlots.find((p) => p.startTime === start && p.endTime === end)
            return preset && preset.label ? `${preset.label} • ${price}` : `${price} • ${start} – ${end}`
        },
        // configuredTarifs use camelCase (timeStart/vatRate); PlanningModal.pushItems reads snake_case.
        tarifToPlanningPrice(t) {
            const ttc = parseFloat(t.price)
            const vat = parseFloat(t.vatRate)
            return {
                start_time: t.timeStart,
                end_time: t.timeEnd,
                price: t.price,
                price_ht: isNaN(ttc) || isNaN(vat) ? '' : (ttc / (1 + vat / 100)).toFixed(2),
                vat_rate: t.vatRate,
                off_peak_time: t.off_peak_time,
                authorize_mixing: t.authorize_mixing,
            }
        },
        openPlanningManual() {
            this.planningMode = 'create'
            this.planningSelectedSlot = this.buildSlotFromCheckedDates(null)
            this.planningResetBlocked = this.isResetBlocked([...this.planningCheckedDates].sort(), this.planningSelectedSlot)
            this.showPlanningModal = true
        },
        onPlanningDateClick(info) {
            this.planningSelectedSlot = this.buildSlotFromCheckedDates(info.dateStr)
            this.planningMode = 'create'
            this.planningResetBlocked = this.isResetBlocked([info.dateStr], this.planningSelectedSlot)
            this.showPlanningModal = true
        },
        // Une règle d'une AUTRE plage (dateStart|dateEnd différents de la sélection) couvre-t-elle
        // l'une des dates ? Alors vider tous les prix dans le modal ne suffirait pas à revenir au
        // prix de base : le serveur garderait la plage (précédence par groupe). Miroir d'affichage.
        isResetBlocked(dates, slot) {
            if (!slot) return false
            const selStart = (slot.startStr || slot.start || '').toString().slice(0, 10)
            const selEnd = (slot.endStr || slot.end || selStart).toString().slice(0, 10)
            for (const d of dates || []) {
                for (const { t } of this.effectivePlanning.get(d) || []) {
                    const rStart = (t.dateStart || '').toString().slice(0, 10)
                    const rEnd = t.dateEnd ? t.dateEnd.toString().slice(0, 10) : SENTINEL_END_DATE
                    if (rStart !== selStart || rEnd !== selEnd) return true
                }
            }
            return false
        },
        onPlanningEventClick(info) {
            this.planningMode = 'edit'
            const eventDate = (info.event.startStr || '').substring(0, 10)

            // Recover the saved prices for this date from the EFFECTIVE rules (camel→snake) so
            // re-opening a day pre-fills all of its créneaux instead of blank rows. The synthetic
            // event id is passed through untouched: onPlanningSubmitRule replaces by group key.
            let planningPrices = null
            const planningId = info.event.id || null
            const matches = this.matchedTarifsForDates([eventDate])
            if (matches.length > 0) {
                planningPrices = matches.map((t) => this.tarifToPlanningPrice(t))
            }

            // Get selectedDays from configuredTarifs if available
            let selectedDays = []
            if (matches.length > 0 && matches[0].selectedDays) {
                selectedDays = [...matches[0].selectedDays]
            }

            // Whole-day single-day edit: scope the slot to the clicked day. Use eventDate (a string from
            // event.startStr) — event.start/.end are Date objects in FullCalendar v5, so calling .substring
            // on them would throw a TypeError.
            this.planningSelectedSlot = {
                dateStr: eventDate,
                start: eventDate,
                startStr: eventDate,
                end: eventDate,
                endStr: eventDate,
                planningPrices: planningPrices,
                planningId: planningId,
                selectedDays: selectedDays,
            }
            this.planningResetBlocked = this.isResetBlocked([eventDate], this.planningSelectedSlot)
            this.showPlanningModal = true
        },
        onPlanningSubmitRule(payload) {
            const { days, range, items, planningId } = payload
            const dateStart = range?.startDate ? String(range.startDate).slice(0, 10) : new Date().toISOString().slice(0, 10)
            const dateEnd = range?.endDate ? String(range.endDate).slice(0, 10) : dateStart

            const newRules = items.map((item) => ({
                id: 'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                dateStart: dateStart,
                dateEnd: dateEnd,
                timeStart: item.startTime ? item.startTime.substring(0, 5) : '00:00',
                timeEnd: item.endTime ? item.endTime.substring(0, 5) : '00:00',
                price: item.price,
                // Always the variant's own VAT: the server ignores any per-créneau rate
                // (PLPRB-2501), so mirroring the item would show a value that is never stored.
                vatRate: this.selectedVariant ? this.selectedVariant.tva : '',
                authorize_mixing: item.authorize_mixing !== undefined ? !!item.authorize_mixing : true,
                off_peak_time: item.off_peak_time !== undefined ? !!item.off_peak_time : true,
                selectedDays: days && days.length ? [...days] : this.getDaysBetween(dateStart, dateEnd).split(', '),
                planningId: planningId || null,
            }))

            const variant = this.variants[this.selectedIndex]
            if (!variant.configuredTarifs) {
                this.$set(variant, 'configuredTarifs', [])
            }

            // La soumission remplace localement les règles de la MÊME clé (plage + jours). Sur les
            // dates qu'elle partage avec une règle plus large, le serveur applique « la dernière
            // soumission fait foi » (syncConfiguredTarifs) et effectivePlanning en montre déjà le
            // résultat. Aucun item (créneaux perso, tous les prix vidés) ⇒ ces règles sont retirées.
            const key = this.tarifGroupKey(
                newRules[0] || { dateStart, dateEnd, selectedDays: days && days.length ? [...days] : [] }
            )
            variant.configuredTarifs = variant.configuredTarifs.filter((e) => this.tarifGroupKey(e) !== key)
            variant.configuredTarifs.push(...newRules)

            this.showPlanningModal = false

            this.$nextTick(() => {
                if (this.$refs.planningCalendar) {
                    const api = this.$refs.planningCalendar.$refs.fullCalendar?.getApi()
                    if (api) api.refetchEvents()
                }
            })
        },

        getVariantRulesCount(variant) {
            // Une « règle tarifaire » = un type de règle activé (dégressif, prix spécifiques),
            // pas le nombre de paliers/créneaux. Méthode réactive : le listing se met à jour
            // automatiquement au basculement des switches.
            let count = 0
            if (variant.hasDegressif) count += 1
            if (variant.hasSpecificPricing) count += 1
            return count
        },

        getVariantPriceDisplay(variant) {
            let prices = [Number(variant.price)]
            if (variant.hasDegressif && variant.paliers && variant.paliers.length > 0) {
                prices = [...prices, ...variant.paliers.map((p) => Number(p.price))]
            }
            if (variant.hasSpecificPricing && variant.configuredTarifs && variant.configuredTarifs.length > 0) {
                prices = [...prices, ...variant.configuredTarifs.map((t) => Number(t.price))]
            }
            prices = prices.filter((p) => !isNaN(p))
            if (prices.length > 1) {
                const min = Math.min(...prices)
                const max = Math.max(...prices)
                return `${min}€-${max}€`
            }
            return `${variant.price || 0}€`
        },

        getPalierDiscount(pIndex) {
            const variant = this.selectedVariant
            const basePrice = Number(variant.price) || 0
            if (basePrice <= 0) return 0

            const palierPrice = Number(variant.paliers[pIndex]?.price) || 0
            if (palierPrice <= 0 || palierPrice >= basePrice) return 0

            return Math.round(100 - (palierPrice / basePrice) * 100)
        },

        // Validation helpers
        getVariantState(vIndex, field) {
            const key = `variants.${vIndex}.${field}`
            if (this.errors[key]) return 'error'
            const val = this.variants[vIndex][field]
            if (val !== null && val !== undefined && val !== '') return 'valid'
            return 'default'
        },
        getVariantError(vIndex, field) {
            return this.errors[`variants.${vIndex}.${field}`] || ''
        },
        hasVariantErrors(vIndex) {
            return Object.keys(this.errors).some((key) => key.startsWith(`variants.${vIndex}.`))
        },
        clearVariantError(vIndex, field) {
            const key = `variants.${vIndex}.${field}`
            if (this.errors[key]) {
                this.$delete(this.errors, key)
            }
        },
        getPalierState(pIndex, field) {
            const key = `variants.${this.selectedIndex}.paliers.${pIndex}.${field}`
            if (this.errors[key]) return 'error'
            const val = this.selectedVariant.paliers[pIndex][field]
            if (val !== null && val !== undefined && val !== '') return 'valid'
            return 'default'
        },
        getPalierError(pIndex, field) {
            return this.errors[`variants.${this.selectedIndex}.paliers.${pIndex}.${field}`] || ''
        },
        clearPalierError(pIndex, field) {
            const key = `variants.${this.selectedIndex}.paliers.${pIndex}.${field}`
            if (this.errors[key]) {
                this.$delete(this.errors, key)
            }
        },
        validateForm() {
            this.errors = {}
            this.variants.forEach((variant, vIndex) => {
                if (!variant.name || !variant.name.trim()) {
                    this.$set(this.errors, `variants.${vIndex}.name`, this.$t('experience.errors.required'))
                }
                if (variant.price === '' || variant.price === null || variant.price === undefined || Number(variant.price) < 0) {
                    this.$set(this.errors, `variants.${vIndex}.price`, this.$t('experience.errors.required'))
                }
                if (variant.tva === '' || variant.tva === null || variant.tva === undefined) {
                    this.$set(this.errors, `variants.${vIndex}.tva`, this.$t('experience.errors.required'))
                }
                if (variant.hasDegressif) {
                    variant.paliers.forEach((palier, pIndex) => {
                        if (!palier.nb_billets || Number(palier.nb_billets) < 1) {
                            this.$set(
                                this.errors,
                                `variants.${vIndex}.paliers.${pIndex}.nb_billets`,
                                this.$t('experience.errors.required')
                            )
                        }
                        if (palier.price === '' || palier.price === null || palier.price === undefined) {
                            this.$set(
                                this.errors,
                                `variants.${vIndex}.paliers.${pIndex}.price`,
                                this.$t('experience.errors.required')
                            )
                        }
                    })
                }
            })
            return Object.keys(this.errors).length === 0
        },
        async submitForm() {
            if (!this.validateForm()) return
            const success = await this.saveExperience(false)
            if (success) {
                this.$toast.success(this.$t('experience.saved'))
                if (!this.complited) {
                    this.$emit('next-step')
                }
            } else {
                console.error('❌ Impossible de passer au step suivant - erreur de sauvegarde')
            }
        },
        async saveExperience(redirect = false) {
            this.loading = true
            const formData = {
                step: 5,
                is_ticket_options: true,
                selectedOptionTarifMode:
                    this.$store.getters['experience/userSelections']?.price_per_person === 1
                        ? 'price per person'
                        : 'price per group',
                min_ticket: 1,
                max_ticket: 9999,
                ticket_variants: this.variants.map((v) => ({
                    id: v.id || null,
                    name: v.name,
                    price: v.price,
                    tva: v.tva,
                    has_degressif: v.hasDegressif,
                    is_undated: !!v.isUndated,
                    has_specific_pricing: !!v.hasSpecificPricing,
                    paliers:
                        v.hasDegressif && v.paliers ? v.paliers.map((p) => ({ nb_billets: p.nb_billets, price: p.price })) : [],
                    configuredTarifs: v.hasSpecificPricing && v.configuredTarifs ? v.configuredTarifs : [],
                })),
            }
            if (this.prestation?.id) formData.id = this.prestation.id

            try {
                const response = await axios.post('/api/v3/experience/save_exit_step_two', formData)
                this.$emit('update-prestation', response.data.data)
                this.initialDataString = this.getFormDataString()

                // If we have just created a variant, we need its ID to load planning
                this.loadVariantsFromPrestation(response.data.data)

                if (redirect) {
                    window.location.href = `${window.location.protocol}//${window.location.host}/back-office/v3/experience`
                }
                return true
            } catch (err) {
                console.error('❌ StepTicketOptions - Save error:', err.response || err)
                if (err.response?.status === 422 && err.response?.data?.errors) {
                    this.errors = err.response.data.errors
                    // Cohérence des tarifs horaires avec l'étape Disponibilités (Step5Request) :
                    // le message porte le nom de la variante, il est aussi affiché sous le calendrier.
                    const tarifKey = Object.keys(this.errors).find((key) => key.includes('.configuredTarifs.'))
                    if (tarifKey && this.$toast) {
                        const message = this.errors[tarifKey]
                        this.$toast.error(Array.isArray(message) ? message[0] : message)
                    }
                }
                return false
            } finally {
                this.loading = false
            }
        },
        loadVariantsFromPrestation(prestationData = null) {
            const data = prestationData || this.prestation
            const options = data?.tickets_as_option || data?.ticketsAsOption
            // Doublons hérités retirés au chargement : annoncés, car le prochain enregistrement de
            // l'étape les supprimera de la base (le planning est réécrit en entier).
            let droppedDuplicates = 0
            if (Array.isArray(options) && options.length > 0) {
                this.variants = options.map((opt) => {
                    const item = opt.item_prestation || opt.itemPrestation || {}
                    const prices = item.prices_by_times || item.pricesByTimes || []
                    const firstPrice = prices[0] || {}
                    const lots = firstPrice.lot_prices_duration || firstPrice.lotPricesDuration || []
                    const isUndated = item.voucher === 1 || item.voucher === '1' || item.voucher === true

                    const planningRecords = item.prestation_planning || item.prestationPlanning || []
                    const hasSpecificPricing = Array.isArray(planningRecords) ? planningRecords.length > 0 : !!planningRecords

                    let configuredTarifs = []
                    if (hasSpecificPricing && Array.isArray(planningRecords)) {
                        // Doublons hérités : deux lignes aux MÊMES dates et aux MÊMES horaires. Seule la plus
                        // ancienne (plus petit id) est facturée (getPrestationPrice, orderBy id) ; l'autre
                        // formait un chevauchement qui bloquait tout enregistrement de l'étape (422). On ne
                        // garde que la ligne facturée — le prix vendu ne change pas (revue 2026-10-01, billet 711).
                        const seenWindows = new Set()
                        const byId = [...planningRecords].sort((a, b) => (a.id || 0) - (b.id || 0))
                        configuredTarifs = byId
                            .flatMap((planning) => {
                                const endDate =
                                    planning.end_date && planning.end_date !== SENTINEL_END_DATE ? planning.end_date : null
                                const planningPrices = planning.planning_prices || planning.planningPrices || []
                                return planningPrices.map((pp) => ({
                                    dateStart: planning.start_date,
                                    // Sentinelle (sans fin) ⇒ dateEnd null : la règle reste ouverte et se
                                    // re-sauve en sentinelle (revue 2026-09-30, constat 7).
                                    dateEnd: endDate,
                                    timeStart: (pp.start_time || '').substring(0, 5),
                                    timeEnd: (pp.end_time || '').substring(0, 5),
                                    price: parseFloat(pp.price || 0).toFixed(2),
                                    vatRate: pp.vat_rate,
                                    authorize_mixing: pp.authorize_mixing !== undefined ? !!pp.authorize_mixing : true,
                                    off_peak_time: pp.off_peak_time !== undefined ? !!pp.off_peak_time : true,
                                    selectedDays: this.getDaysBetween(planning.start_date, endDate).split(', '),
                                    planningId: planning.id || null,
                                }))
                            })
                            .filter((t) => {
                                const key = `${t.dateStart}|${t.dateEnd || ''}|${t.timeStart}|${t.timeEnd}`
                                if (seenWindows.has(key)) {
                                    droppedDuplicates += 1
                                    return false
                                }
                                seenWindows.add(key)
                                return true
                            })
                    }

                    return {
                        id: item.id || null,
                        name: item.name || '',
                        price: item.price != null ? parseFloat(item.price).toFixed(2) : '',
                        tva: item.vat_rate != null ? Number(item.vat_rate) : '',
                        hasDegressif: lots.length > 0,
                        isUndated,
                        hasSpecificPricing,
                        paliers:
                            lots.length > 0
                                ? lots.map((l) => ({
                                      nb_billets: l.nb_contributor || 1,
                                      price: l.price != null ? parseFloat(l.price).toFixed(2) : '',
                                  }))
                                : [{ nb_billets: 1, price: '' }],
                        configuredTarifs,
                    }
                })
            }
            if (droppedDuplicates > 0 && this.$toast) {
                this.$toast.info(this.$t('experience.stepTicketOptions.duplicateTarifsIgnored', { count: droppedDuplicates }))
            }
        },
        getDaysBetween(startDate, endDate) {
            const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
            if (!endDate) return weekdays.slice(1).concat(weekdays[0]).join(', ')
            const days = []
            // Parse as local midnight ('YYYY-MM-DD' alone is parsed as UTC, which can
            // shift getDay() by a day in negative-UTC timezones).
            const currentDate = new Date(`${String(startDate).slice(0, 10)}T00:00:00`)
            const end = new Date(`${String(endDate).slice(0, 10)}T00:00:00`)
            while (currentDate <= end) {
                days.push(weekdays[currentDate.getDay()])
                currentDate.setDate(currentDate.getDate() + 1)
            }
            return [...new Set(days)].join(', ')
        },
        getFormDataString() {
            return JSON.stringify({
                variants: this.variants,
            })
        },
        hasChanges() {
            if (!this.initialDataString) return false
            return this.initialDataString !== this.getFormDataString()
        },
    },
    watch: {
        prestation: {
            handler(newVal) {
                if (newVal && newVal.id) {
                    this.loadVariantsFromPrestation()
                    this.$nextTick(() => {
                        this.initialDataString = this.getFormDataString()
                    })
                }
            },
            immediate: true,
            deep: true,
        },
        variants: {
            handler() {
                if (!this.initialDataString && this.variants.length > 0) {
                    this.initialDataString = this.getFormDataString()
                }
            },
            deep: true,
        },
        country_vat_rates: {
            handler() {
                this.setOptionTva()
            },
            deep: true,
            immediate: true,
        },
    },
}
</script>

<style scoped>
/* Layout principal - wrapper full */
.ticket-options-wrapper {
    padding-left: 0;
}

/* Form section (variantes + contenu) : occupe 70% du total */
.ticket-form-section {
    flex: 0 0 70%;
    min-width: 0;
}

/* Sidebar variantes : 20% du total (soit ~28.6% de ses 70%) */
.ticket-variants-sidebar {
    flex: 0 0 28.6%;
    min-width: 180px;
    border-right: 1px solid rgba(84, 85, 149, 0.12);
}

.variant-sidebar-heading {
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #534ab7;
    font-weight: 600;
    margin: 0 0 12px;
    padding: 0;
}

/* Panneau interne lavande */
.variant-panel {
    background: #eeedfe;
    border: 0.5px solid #cecbf6;
    border-radius: 12px;
    padding: 8px;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.variant-list {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
}

.variant-row {
    position: relative;
    padding: 9px 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s ease;
}

.variant-row + .variant-row::before {
    content: '';
    position: absolute;
    left: 8px;
    right: 8px;
    top: 0;
    height: 0.5px;
    background: #cecbf6;
}

.variant-row:hover:not(.variant-row--active) {
    background: rgba(83, 74, 183, 0.06);
}

.variant-row--active {
    box-shadow: inset 2px 0 0 #7d2dbd;
    border-radius: 8px;
}

.variant-row-inner {
    display: flex;
    align-items: flex-start;
    gap: 4px;
}

.variant-row-body {
    flex: 1;
    min-width: 0;
}

.variant-row-name {
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.variant-row-meta {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-top: 2px;
}

.variant-row-price {
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
}

.variant-row-rules {
    font-size: 11px;
}

.variant-kebab {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    border-radius: 6px;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    transition:
        background 0.15s ease,
        color 0.15s ease;
}

.variant-kebab:hover {
    background: rgb(125, 45, 189, var(--tw-bg-opacity)) !important;
}

/* Détail de la variante : 50% du total (soit ~71.4% de ses 70%) */
.variant-detail-panel {
    flex: 0 0 66.4%;
}

/* Preview à droite : occupe 30% du total */
.ticket-options-wrapper ::v-deep .preview-section {
    flex: 0 0 30% !important;
}

/* Override SectionCard border/radius to match Figma */
.ticket-section-card {
    border-radius: 16px !important;
    border-color: rgba(156, 154, 248, 0.2) !important;
}

/* Bouton Ajouter variante — même niveau que .variant-panel */
.add-variant-sidebar-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 8px;
    width: 100%;
    padding: 9px;
    border-radius: 8px;
    border: 0.5px dashed #afa9ec;
    background: transparent;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease;
    flex-shrink: 0;
}

.add-variant-sidebar-btn:hover {
    background: rgb(125, 45, 189, var(--tw-bg-opacity));
}
.add-palier-dash-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 9px;
    border: 1px dashed #9c9af880;
    border-radius: 12px;
    background: transparent;
    color: #9c9af8;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 100%;
}
.add-palier-dash-btn:hover {
    background: rgba(156, 154, 248, 0.05);
    border-color: #9c9af8;
}

.menuDropdown {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}
</style>
